/**
 * MVP module — IA category scorer (Phase B milestone B2).
 * Policy anchor: MVP guide §4, delta doc §1.3.
 *
 * MVP IA is BINARY: ≥1 attested/selected activity present in the pathway's IA
 * inventory → 40/40 = 100%; else 0% with a hint listing qualifying activities.
 * No activity weights, no special-status doubling — one activity is the floor
 * for everyone under an MVP. The what-if `iaWillAttest` assumption overrides.
 */
import type { ParameterSet } from './parameters/py2026';

export interface MvpIaResult {
  pct: number;
  detail: {
    qualifying: string[];              // practice activities ∩ pathway inventory
    attestAssumption: boolean | null;  // explicit what-if override, if any
    hint: string;
  };
}

export function scoreMvpIa(args: {
  iaActivityIds: string[];
  iaPool: string[];                    // pathway's iaMeasureIds
  iaWillAttest?: boolean;              // what-if override
  params: ParameterSet;
}): MvpIaResult {
  const { iaActivityIds, iaPool, iaWillAttest, params } = args;
  const poolSet = new Set(iaPool);
  const qualifying = iaActivityIds.filter(id => poolSet.has(id));

  const attests = iaWillAttest ?? qualifying.length >= params.mvpIaRequiredActivities;
  const pct = attests ? 100 : 0;

  return {
    pct,
    detail: {
      qualifying,
      attestAssumption: iaWillAttest ?? null,
      hint: attests
        ? `Full credit: ${params.mvpIaPointsPerActivity}/${params.iaCategoryMaxPoints} (1 qualifying activity suffices under an MVP)`
        : `No current activity qualifies — attest to any 1 of the pathway's ${iaPool.length} activities for full credit`,
    },
  };
}
